/**
 * Read hook for the thumbnail panel — everything derives from the doc in one
 * pass, re-run on any thumbnail write (same rule as the design hooks).
 */
import type { ThumbJobState, ThumbPortraitInfo, ThumbShotInfo } from "@kola/shared";
import { useMemo } from "react";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import { groupThumbShots, type ThumbRunGroup } from "./tree.js";

export interface ThumbnailsView {
  title: string;
  text: string;
  imageModel: string | undefined;
  textModel: string | undefined;
  /** Presenter first. */
  portraits: ThumbPortraitInfo[];
  shots: ThumbShotInfo[];
  /** The gallery's shape: runs newest first, refinements under their roots. */
  runs: ThumbRunGroup[];
  /** The team's pick, if any. */
  selectedId: string | undefined;
  /** Live run state, read-only (the server is its only writer). */
  job: ThumbJobState | undefined;
}

export function useThumbnails(session: BoardSession): ThumbnailsView {
  const thumbsVersion = useStore(session.store, (s) => s.thumbsVersion);
  return useMemo(() => {
    void thumbsVersion; // re-derive on any thumbnail change
    const shots = session.board.listThumbShots();
    return {
      title: session.board.getThumbTitle(),
      text: session.board.getThumbText(),
      imageModel: session.board.getThumbImageModel(),
      textModel: session.board.getThumbTextModel(),
      portraits: session.board.listThumbPortraits(),
      shots,
      runs: groupThumbShots(shots),
      selectedId: session.board.getSelectedThumb(),
      job: session.board.getThumbJob(),
    };
  }, [session, thumbsVersion]);
}
